import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
  useMemo,
} from "react";
import {
  AudioRecorderError,
  useAudioRecorder,
} from "@web-src/modules/chats/hooks/useAudioRecorder";
import { useToast, useTranslations } from "@jugl-web/utils";
import { PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { ChatAudioPlayer } from "./components/ChatAudioPlayer";
import { ChatInputButton } from "../ChatInputButton";
import { ReactComponent as StopIcon } from "./assets/stop.svg";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";

const maxRecordingTimeInMinutes = 15;
export interface ChatInputAudioRecorderHandle {
  stop: () => void;
  reset: () => void;
}

export const ChatInputAudioRecorder = forwardRef<
  ChatInputAudioRecorderHandle,
  {
    onCancel: () => void;
    onSend: (file: File) => void;
    defaultRecordingFile?: File;
    onRecordingAvailable?: (file: File) => void;
    isLoading: boolean;
  }
>(
  (
    { onCancel, onSend, isLoading, onRecordingAvailable, defaultRecordingFile },
    ref
  ) => {
    const { t } = useTranslations();
    const { toast } = useToast();
    const [autoSend, setAutoSend] = useState(false);

    const maxRecordingTime = maxRecordingTimeInMinutes * 60;

    const { stop, lastRecordedFile, error, timeInSeconds, timeStr, reset } =
      useAudioRecorder({
        maxTimeInSeconds: maxRecordingTime,
        autoStart: true,
      });

    const recordingFile = useMemo(() => {
      if (defaultRecordingFile) {
        return {
          file: defaultRecordingFile,
          url: URL.createObjectURL(defaultRecordingFile),
        };
      }
      return lastRecordedFile;
    }, [defaultRecordingFile, lastRecordedFile]);

    useEffect(() => {
      if (lastRecordedFile) {
        reset();
        onRecordingAvailable?.(lastRecordedFile.file);
      }
    }, [lastRecordedFile, onRecordingAvailable, reset]);

    useImperativeHandle(
      ref,
      () => ({
        stop,
        reset,
      }),
      [stop, reset]
    );

    useEffect(() => {
      if (error === AudioRecorderError.noPermissions) {
        toast(
          t({
            id: "feedback.no-recording-permissions",
            defaultMessage: "No Audio Recording permissions",
          }),
          {
            variant: "error",
          }
        );
        onCancel?.();
      }
    }, [error, onCancel, toast, t]);

    const handleSendClick = useCallback(() => {
      if (!recordingFile) {
        return;
      }

      onSend?.(recordingFile.file);
      reset();
    }, [recordingFile, onSend, reset]);

    useEffect(() => {
      if (recordingFile && autoSend) {
        handleSendClick();
        setAutoSend(false);
      }
    }, [handleSendClick, recordingFile, autoSend]);

    return (
      <div>
        <div className="flex w-full items-center justify-start gap-4">
          {recordingFile ? (
            <ChatAudioPlayer url={recordingFile.url} />
          ) : (
            <>
              <div className="flex items-center gap-1">
                <span className="text-xs leading-[130%] text-[#707577]">
                  {timeStr}
                </span>
                <div className="h-2 w-2 rounded-full bg-gradients-danger" />
              </div>
              <div className="h-1 w-full overflow-hidden rounded bg-grey-200">
                <div
                  className="h-1 animate-[fill] bg-primary"
                  style={{
                    animation: `fill ${maxRecordingTime}s linear forwards`,
                  }}
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-7">
            <Tooltip
              placement="top"
              renderTrigger={({ props, ref: triggerRef }) => (
                <PlainButton
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-grey-200 transition-colors hover:bg-grey-300"
                  onClick={() => {
                    reset();
                    onCancel();
                  }}
                  ref={triggerRef}
                  {...props}
                >
                  <DeleteIcon />
                </PlainButton>
              )}
            >
              {t({
                id: "common.delete",
                defaultMessage: "Delete",
              })}
            </Tooltip>
            {!recordingFile && (
              <Tooltip
                placement="top"
                renderTrigger={({ props, ref: triggerRef }) => (
                  <PlainButton
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-grey-200 transition-colors hover:bg-grey-300"
                    onClick={stop}
                    ref={triggerRef}
                    {...props}
                  >
                    <StopIcon />
                  </PlainButton>
                )}
              >
                {t({
                  id: "common.stop",
                  defaultMessage: "Stop",
                })}
              </Tooltip>
            )}
            <Tooltip
              placement="top"
              renderTrigger={({ props, ref: triggerRef }) => (
                <div ref={triggerRef} {...props}>
                  <ChatInputButton
                    onClick={() => {
                      if (!recordingFile) {
                        setAutoSend(true);
                        stop();
                        return;
                      }

                      handleSendClick();
                    }}
                    isLoading={isLoading || autoSend}
                    isDisabled={isLoading || autoSend}
                  />
                </div>
              )}
            >
              {t({
                id: "common.send",
                defaultMessage: "Send",
              })}
            </Tooltip>
          </div>
        </div>
        {timeInSeconds >= maxRecordingTime - 60 && (
          <div className="font-secondary text-xs leading-2 text-gradients-danger">
            {t(
              {
                id: "chats-page.max-recording-durations-minutes",
                defaultMessage:
                  "Max recording durations is {maxRecordingTimeInMinutes} minutes",
              },
              {
                maxRecordingTimeInMinutes,
              }
            )}
          </div>
        )}
      </div>
    );
  }
);
